/**
 * The sheet a campaign push opens.
 *
 * Campaign pushes have always carried a deep link
 * (services/campaigns/merge#campaignDeepLink) and it opened nothing — a
 * notification that leads nowhere is worse than no notification. The push-tap
 * handler in RootNavigator calls showCampaignMessage() and this presents,
 * fetching that step's content from GET /campaign-content.
 *
 * The body is rendered with react-native-render-html, the renderer the email
 * template editor and the campaign step preview already use for the same
 * template HTML. A WebView would be more faithful to the email, but it would
 * mean a second web engine on screen, a sandbox to police, and no shared idiom
 * with the two surfaces already rendering these bodies. The divergence it costs
 * is small by design: the API deliberately strips the layout chrome, the
 * tracking pixel, the click-redirects and the unsubscribe footer, so what is
 * left is the authored message rather than the email as sent.
 *
 * Three content shapes, all handled: an email step (html, subject as the
 * header), a push-only step (no html — the notification copy IS the content),
 * and neither, which the API answers with a 404 but which must not crash here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import RenderHtml from 'react-native-render-html';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';
import { getSessionIds } from '../store/actions/_sessionIds';
import debug from '../utilities/debug';
import {
  fetchCampaignContent,
  type CampaignContent,
} from '../utilities/campaignContentApi';
import {
  PendingCampaignMessage,
  subscribeCampaignMessage,
  clearCampaignMessage,
} from '../utilities/campaignMessageStore';

const UNAVAILABLE = 'That campaign message is not available.';

// The email bodies assume a light canvas and set their own text colours inline,
// so rendering them straight onto a dark sheet produces dark-on-dark text. The
// body gets its own light surface — which also reads as "this is the message we
// sent you" rather than a screen of the app.
const PAPER = '#FFFFFF';
const PAPER_INK = '#1A1A1A';

export default function CampaignMessageSheet(): React.JSX.Element {
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const sheet = useSheetLayout({ desktopMaxWidth: 560 });
  const [pending, setPending] = useState<PendingCampaignMessage | null>(null);
  const [content, setContent] = useState<CampaignContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => subscribeCampaignMessage(setPending), []);

  useEffect(() => {
    if (!pending) {
      setContent(null);
      setFailure(null);
      return;
    }
    let cancelled = false;
    setContent(null);
    setFailure(null);
    setLoading(true);
    (async () => {
      try {
        const { accountId, cognitoId } = getSessionIds();
        const res = await fetchCampaignContent(accountId, cognitoId, {
          enrollmentId: pending.enrollmentId,
          stepKey: pending.stepKey,
        });
        if (cancelled) return;
        // apiGet resolves a 404 into { success: false, message } rather than
        // throwing, and every refusal carries the same message on purpose —
        // there is nothing to distinguish, so don't try.
        if (res?.success) {
          setContent({
            campaign: res.campaign ?? { key: null, name: null },
            step_key: res.step_key ?? pending.stepKey,
            subject: res.subject ?? null,
            html: res.html ?? null,
            push: res.push ?? null,
          });
        } else {
          setFailure(res?.message || UNAVAILABLE);
        }
      } catch (err: any) {
        if (cancelled) return;
        debug.warn('[campaign-sheet] load failed', { err: err?.message });
        setFailure(UNAVAILABLE);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pending]);

  const dismiss = useCallback(() => clearCampaignMessage(), []);

  // While the fetch is in flight the notification's own copy stands in, so the
  // sheet opens with the message the member just tapped rather than a spinner.
  const heading = content?.subject
    || content?.push?.title
    || pending?.title
    || content?.campaign?.name
    || pending?.campaignKey
    || 'Your message';
  const pushBody = content?.push?.body || pending?.body || null;
  const contentWidth = Math.max(240, Math.min(width, sheet.isDesktop ? 560 : width) - 64);

  return (
    <Modal
      visible={!!pending}
      transparent
      animationType={sheet.animationType}
      onRequestClose={dismiss}
    >
      <Pressable
        style={[{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }, sheet.overlayStyle]}
        onPress={dismiss}
      >
        <Pressable
          testID="campaign-sheet"
          style={[
            {
              backgroundColor: theme.colors.card,
              borderTopLeftRadius: theme.radius.lg,
              borderTopRightRadius: theme.radius.lg,
              paddingHorizontal: theme.spacing.lg,
              paddingBottom: theme.spacing.xl,
              maxHeight: '85%',
            },
            sheet.sheetStyle,
          ]}
          onPress={() => {}}
        >
          {sheet.showHandle && (
            <View
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                backgroundColor: theme.colors.border,
                alignSelf: 'center',
                marginTop: theme.spacing.sm,
                marginBottom: theme.spacing.md,
              }}
            />
          )}

          {!!content?.campaign?.name && (
            <Text
              style={{
                fontSize: theme.font.size.xs,
                fontWeight: theme.font.weight.bold,
                color: theme.colors.muted,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                marginBottom: 4,
              }}
            >
              {content.campaign.name}
            </Text>
          )}

          <Text
            style={{
              fontSize: theme.font.size.xl,
              fontWeight: theme.font.weight.bold,
              color: theme.colors.text,
              marginBottom: theme.spacing.sm,
            }}
          >
            {heading}
          </Text>

          <ScrollView
            style={{ marginBottom: theme.spacing.md }}
            contentContainerStyle={{ paddingBottom: theme.spacing.sm }}
          >
            {failure ? (
              <Text style={{ fontSize: theme.font.size.md, lineHeight: 22, color: theme.colors.muted }}>
                {failure}
              </Text>
            ) : content?.html ? (
              <View
                style={{
                  backgroundColor: PAPER,
                  borderRadius: theme.radius.md,
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                }}
              >
                <RenderHtml
                  contentWidth={contentWidth}
                  source={{ html: content.html }}
                  baseStyle={{ color: PAPER_INK, fontSize: 15, lineHeight: 22 }}
                />
              </View>
            ) : pushBody ? (
              // A push-only step (skip_email): the notification copy is the
              // content, so show it rather than dead-ending the member.
              <Text style={{ fontSize: theme.font.size.md, lineHeight: 22, color: theme.colors.text }}>
                {pushBody}
              </Text>
            ) : loading ? (
              <ActivityIndicator color={theme.colors.primary} style={{ marginVertical: theme.spacing.lg }} />
            ) : (
              <Text style={{ fontSize: theme.font.size.md, lineHeight: 22, color: theme.colors.muted }}>
                {UNAVAILABLE}
              </Text>
            )}
          </ScrollView>

          <Pressable
            testID="campaign-dismiss"
            accessibilityRole="button"
            onPress={dismiss}
            style={{
              backgroundColor: theme.colors.primary,
              borderRadius: theme.radius.md,
              paddingVertical: theme.spacing.md,
              alignItems: 'center',
            }}
          >
            <Text style={{ color: '#fff', fontSize: theme.font.size.md, fontWeight: theme.font.weight.bold }}>
              Done
            </Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
